import React from 'react';

import { LumioGlyph } from '../LumioMark';
import { Root, Label, CostGroup, Sep, CostText } from './GenerateButton.styles';

export type GenerateButtonProps = {
  /** Already-translated button copy (e.g. `t('Generate')`). */
  label: string;
  /** Credit cost of the action; the inline cost is shown only once it's known. */
  cost?: number;
  disabled?: boolean;
  /** Shrink to fit its content instead of filling the row (full width is the default). Used where the CTA
   *  shares a compact bar rather than owning a full-width footer (the immersive storyboard editor). */
  compact?: boolean;
  /** The outline (secondary) look, for a Lumio action that is not the screen's main call to action. */
  secondary?: boolean;
  onClick: () => void;
};

/**
 * Lumio generate CTA: the action label, then a subtle divider and the cost inline as a solid Lumio
 * spark (in the button's own text colour) + "{n} credits" — the cost reads on the action itself, so
 * no "This costs N credits" tagline is needed beneath it. Used by the edit sheet and the custom studio.
 */
export const GenerateButton = ({
  label,
  cost,
  disabled = false,
  compact = false,
  secondary = false,
  onClick,
}: GenerateButtonProps): React.ReactElement => (
  <Root type="button" $compact={compact} $secondary={secondary} disabled={disabled} onClick={onClick}>
    <Label text={label} />
    {cost != null ? (
      <CostGroup>
        <Sep aria-hidden="true">|</Sep>
        <LumioGlyph size={15} />
        <CostText text={cost === 1 ? '{{n}} credit' : '{{n}} credits'} placeholders={{ n: cost }} />
      </CostGroup>
    ) : null}
  </Root>
);

export default GenerateButton;
